/**
 * Clearing the organisation's data from a workbench or a node (packages/shared/src/schemas/deviceClear.ts): the
 * dialog that asks for it, and the line that says how the last request went. The machine carries it out the next
 * time it is in touch, removed or not, and reports what it deleted.
 */
import type { DeviceClearRecord, DeviceClearScope } from '@aioe/shared';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { ChoiceSelect, ConfirmDialog, ErrorNote, inputClass, StatusMark, Tick } from './ui.tsx';

const SCOPES: Record<DeviceClearScope, string> = {
  organisation: "The organisation's data only",
  'organisation-and-repositories': "The organisation's data and its repositories' working copies",
};

export function ClearOrganisationDataDialog({ deviceId, name, kind, onClose, onDone }: { deviceId: string; name: string; kind: 'workbench' | 'node'; onClose: () => void; onDone: () => void }) {
  const [scope, setScope] = useState<DeviceClearScope>('organisation');
  const [remove, setRemove] = useState(true);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  return (
    <ConfirmDialog
      isOpen
      danger
      title={`Clear the organisation's data from ${name}?`}
      confirmLabel={remove ? 'Remove and clear' : 'Clear its data'}
      onCancel={onClose}
      onConfirm={async () => {
        setError(undefined);
        try {
          await routes.clearDevice(deviceId, { scope, remove, ...(reason.trim() ? { reason: reason.trim() } : {}) });
          onDone();
          onClose();
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }}
    >
      <p>
        The {kind} deletes what it holds of this organisation the next time it is in touch: its sign-in, the organisation's teams, policy, catalogue and design systems, its cached projects, boards,
        memory and inbox, the organisation's secrets and keys{kind === 'node' ? ', and the work it was holding' : ''}. It then reports what it deleted. This cannot be undone.
      </p>
      <p>The person's own work outside the organisation stays.</p>
      <ChoiceSelect label="What to delete" value={scope} options={SCOPES} onChange={setScope} />
      {scope === 'organisation-and-repositories' ? <p>The working copies of the organisation's projects' repositories on that machine go too, including changes nobody pushed.</p> : null}
      <Tick checked={remove} onChange={setRemove} label="Also remove it from the organisation">
        Also remove it from the organisation, so it cannot reconnect without enrolling again
      </Tick>
      <input className={inputClass} aria-label="Why, for the audit trail" placeholder="Why, for the audit trail (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
      <ErrorNote error={error} />
    </ConfirmDialog>
  );
}

const STATE: Record<DeviceClearRecord['state'], { mark: 'working' | 'online' | 'error'; words: string }> = {
  pending: { mark: 'working', words: 'Waiting for it to be in touch' },
  delivered: { mark: 'working', words: 'Told; waiting for its report' },
  done: { mark: 'online', words: 'Cleared' },
  failed: { mark: 'error', words: 'Could not clear everything' },
};

/** The last request and its outcome, in words; nothing when there has been none. */
export function ClearOrganisationDataStatus({ deviceId, version }: { deviceId: string; version?: number }) {
  const clears = useLoad(() => routes.deviceClears(deviceId).catch(() => ({ clears: [] as DeviceClearRecord[] })), [deviceId, version]);
  const last = clears.data?.clears[0];
  if (!last) return null;
  const s = STATE[last.state];
  return (
    <div className="mb-4 grid gap-1 rounded-lg bg-surface-secondary p-3 text-sm">
      <span className="flex flex-wrap items-center gap-2">
        <StatusMark state={s.mark} label={s.words} />
        <span className="text-xs text-muted">
          {SCOPES[last.scope]}, asked {timeAgo(last.requestedAt)}
          {last.by ? ` by ${last.by}` : ''}
          {last.remove ? ', and removed from the organisation' : ''}
          {last.answeredAt ? `; reported ${timeAgo(last.answeredAt)}` : ''}
        </span>
      </span>
      {last.message ? <p className="text-xs">{last.message}</p> : null}
      {last.repositories?.length ? <p className="mono break-words text-xs text-muted">{last.repositories.join(', ')}</p> : null}
    </div>
  );
}
